import { UseFieldArrayReturn, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ScrollableCellWithAvatar } from '@/components/data-table/cells';
import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatAmountWithCurrencyCode } from '@/helpers/finance';
import { RateMask } from '@/modules/common/utils/rate-mask';

import { InvoiceDetails, InvoiceDetailsItem, InvoiceEntityType } from '../../types';
import { InvoiceLineValidationIssue, UpdateInvoiceFormValues } from '../../validations';
import { AddInvoiceLineButton } from '../add-invoice-line-button';
import { InvoiceEditItemRow } from './invoice-edit-item-row';

interface InvoiceLineItemsCardProps {
  details: InvoiceDetails;
  form: UseFormReturn<UpdateInvoiceFormValues>;
  isReadOnly: boolean;
  noValue: string;
  currencyCode: string;
  fields: UseFieldArrayReturn<UpdateInvoiceFormValues, 'items'>['fields'];
  lineValidationIssues: InvoiceLineValidationIssue[][];
  isEmptyLineItemsError: boolean;
  emptyLineItemsErrorMessage: string;
  onRemove: (index: number) => void;
  onAddUserItem: () => void;
  onAddOverheadItem: () => void;
}

/** Renders invoice line items in either their editable table or static review form. */
export function InvoiceLineItemsCard({
  details,
  form,
  isReadOnly,
  noValue,
  currencyCode,
  fields,
  lineValidationIssues,
  isEmptyLineItemsError,
  emptyLineItemsErrorMessage,
  onRemove,
  onAddUserItem,
  onAddOverheadItem,
}: InvoiceLineItemsCardProps) {
  const { t } = useTranslation(['authenticated.invoices', 'common']);

  return (
    <Card className="overflow-hidden">
      <CardHeader>
        <CardTitle>{t('details.lineItems')}</CardTitle>
      </CardHeader>
      <div className="overflow-x-auto">
        <Table className={isReadOnly ? 'min-w-[720px] table-fixed' : 'min-w-[780px] table-fixed'}>
          <TableHeader>
            <TableRow>
              <TableHead>{t('details.lineItemsHeaders.name')}</TableHead>
              <TableHead className="w-40">{t('details.lineItemsHeaders.totalHours')}</TableHead>
              <TableHead className="w-56">{t('details.lineItemsHeaders.effectiveRate')}</TableHead>
              <TableHead className="w-40">{t('details.lineItemsHeaders.amount')}</TableHead>
              {!isReadOnly && (
                <TableHead className="w-16 pr-6">
                  <span className="sr-only">{t('headers.action')}</span>
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isReadOnly ? (
              <ReadOnlyInvoiceItemRows details={details} noValue={noValue} />
            ) : (
              <>
                {fields.map((field, index) => (
                  <InvoiceEditItemRow
                    key={field.id}
                    details={details}
                    form={form}
                    index={index}
                    currencyCode={currencyCode}
                    validationIssues={lineValidationIssues[index] ?? []}
                    onRemove={() => onRemove(index)}
                  />
                ))}
                <TableRow>
                  <TableCell colSpan={5} className="py-2 pl-6">
                    <AddInvoiceLineButton
                      label={t('details.edit.items.addLine')}
                      onAddOverhead={onAddOverheadItem}
                      onAddMember={onAddUserItem}
                    />
                  </TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </div>
      {isEmptyLineItemsError && (
        <p className="px-6 pb-4 text-sm font-medium text-destructive">
          {t(emptyLineItemsErrorMessage)}
        </p>
      )}
    </Card>
  );
}

function InvoiceItemName({
  item,
  label,
  noValue,
}: {
  item: InvoiceDetailsItem;
  label: string;
  noValue: string;
}) {
  if (item.member) {
    return (
      <ScrollableCellWithAvatar
        text={item.member.name}
        image={item.member.profileImageUrl}
        avatarName={item.member.name}
        avatarSize="sm"
        alt={item.member.name}
        className="min-w-48"
      />
    );
  }

  return (
    <Input value={item.description ?? noValue} aria-label={label} disabled className="min-w-48" />
  );
}

function ReadOnlyInvoiceItemRows({
  details,
  noValue,
}: {
  details: InvoiceDetails;
  noValue: string;
}) {
  const { t } = useTranslation(['authenticated.invoices', 'common']);

  if (details.items.length === 0) {
    return (
      <TableRow>
        <TableCell colSpan={4} className="h-16 text-center text-sm text-muted-foreground">
          {t('common:noResultsFound')}
        </TableCell>
      </TableRow>
    );
  }

  return details.items.map((item, index) => (
    <TableRow key={`${item.entityType}-${item.entityId ?? index}-${index}`}>
      <TableCell>
        <InvoiceItemName item={item} label={t('details.lineItemsHeaders.name')} noValue={noValue} />
      </TableCell>
      <TableCell>
        <Input
          value={RateMask.applyToString(item.quantity ?? undefined) || noValue}
          aria-label={t('details.lineItemsHeaders.totalHours')}
          disabled
          className="min-w-28 max-w-36 tabular-nums"
        />
      </TableCell>
      <TableCell>
        <ReadOnlyRate
          item={item}
          currencyCode={details.currencyCode}
          label={t('details.lineItemsHeaders.effectiveRate')}
          noValue={noValue}
        />
      </TableCell>
      <TableCell className="font-medium tabular-nums">
        {formatAmountWithCurrencyCode(item.amount, details.currencyCode)}
      </TableCell>
    </TableRow>
  ));
}

function ReadOnlyRate({
  item,
  currencyCode,
  label,
  noValue,
}: {
  item: InvoiceDetailsItem;
  currencyCode: string;
  label: string;
  noValue: string;
}) {
  const { t } = useTranslation('common');
  const value = RateMask.applyToString(item.unitPrice ?? undefined) || noValue;

  if (RateMask.isMaskedStringValue(item.unitPrice ?? undefined)) {
    return (
      <Input value={value} aria-label={label} disabled className="min-w-28 max-w-36 tabular-nums" />
    );
  }

  const suffix =
    item.entityType === InvoiceEntityType.User
      ? t('rateUnits.perHour', { currency: currencyCode })
      : currencyCode;

  return (
    <InputGroup disabled className="min-w-0">
      <InputGroupInput
        value={value}
        aria-label={label}
        disabled
        className="w-0 min-w-0 tabular-nums"
      />
      {item.unitPrice && (
        <InputGroupAddon align="inline-end">
          <span>{suffix}</span>
        </InputGroupAddon>
      )}
    </InputGroup>
  );
}
